import { useCallback, useState } from 'react';
import { useQuery, registerQuery, registerMutation, useMutation } from 'data-fetching-client';
import { getClient } from '../data/client';
import { getApiUrl } from '../utils/urls';
import { captureException } from '../utils/captureException';
import { setFavicon, setTabTitle } from '../utils/brandingUtils';
import { genericSubmitErrorAlert, rateLimitErrorAlert } from '../utils/alerts';
import useLocaleContext from './useLocaleContext';
import { quickFetchHelper } from '../utils/quickFetch';
import { CONFIG_EARLY_REQUEST } from '../constants/earlyRequests';
import { getApolloErrorStatusCode } from '../utils/getApolloErrorStatusCode';
const getDataPrivacyConfigQuery = 'getDataPrivacyConfigQuery';
const GET_DATA_PRIVACY_CONFIG = registerQuery({
  fieldName: getDataPrivacyConfigQuery,
  fetcher: ({
    slug
  }) => quickFetchHelper(CONFIG_EARLY_REQUEST, `${getApiUrl()}/${slug}`)
});
const submitDataPrivacyRequestMutation = 'submitDataPrivacyRequestMutation';
const SUBMIT_DATA_PRIVACY_REQUEST_MUTATION = registerMutation({
  fieldName: submitDataPrivacyRequestMutation,
  args: ['payload', 'slug'],
  fetcher: ({
    payload,
    slug
  }) => getClient().post(`${getApiUrl()}/submit/${slug}`, {
    data: payload
  })
});
function useDataPrivacyConfig(slug) {
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const {
    locale
  } = useLocaleContext();
  const {
    data: dataPrivacyConfig,
    loading: loadingConfig,
    error: errorFetchingConfig
  } = useQuery(GET_DATA_PRIVACY_CONFIG, {
    variables: {
      slug
    },
    onError: err => {
      captureException(err, ['Error fetching data privacy config']);
    },
    onCompleted: data => {
      var _data$getDataPrivacyC;
      const branding = data === null || data === void 0 || (_data$getDataPrivacyC = data[getDataPrivacyConfigQuery]) === null || _data$getDataPrivacyC === void 0 ? void 0 : _data$getDataPrivacyC.branding;
      setTabTitle(branding === null || branding === void 0 ? void 0 : branding.companyName);
      setFavicon(branding === null || branding === void 0 ? void 0 : branding.faviconUrl);
    }
  });
  const [submitRequestMutation, {
    loading: loadingSubmit
  }] = useMutation(SUBMIT_DATA_PRIVACY_REQUEST_MUTATION, {
    onCompleted: () => {
      setRequestSubmitted(true);
    },
    onError: err => {
      const statusCode = getApolloErrorStatusCode(err);
      if (statusCode === 429) {
        rateLimitErrorAlert(locale);
      } else {
        genericSubmitErrorAlert(locale);
      }
      captureException(err, ['Error submitting data privacy request']);
    }
  });
  const submitRequest = useCallback(payload => {
    void submitRequestMutation({
      variables: {
        payload,
        slug
      }
    });
  }, [slug, submitRequestMutation]);
  return {
    dataPrivacyConfig: dataPrivacyConfig === null || dataPrivacyConfig === void 0 ? void 0 : dataPrivacyConfig[getDataPrivacyConfigQuery],
    errorFetchingConfig,
    loading: loadingConfig || loadingSubmit,
    requestSubmitted,
    submitRequest
  };
}
export default useDataPrivacyConfig;